import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  CustomInput,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import {
  inferComposerHasVideo,
  tiktokCreatorFlagDisablesFeature,
} from "../../../../cores/socialPost/helpers/tiktokComposerVideo";
import { tiktokApiService } from "../../../../cores/tiktok/services/api";
import {
  getTiktokCreatorInfoFailureAction,
  getTiktokCreatorInfoInitAction,
  getTiktokCreatorInfoSuccessAction,
  hydrateTiktokDirectPostDraftAction,
  resetTiktokDirectPostUiAction,
  setTiktokDirectPostDraftAction,
} from "../../../../cores/tiktok/store/api-actions";
import {
  tiktokCreatorInfoErrorSelector,
  tiktokCreatorInfoLoadingSelector,
  tiktokCreatorInfoSelector,
  tiktokDirectPostDraftSelector,
} from "../../../../cores/tiktok/store/selector";

/** Títulos cortos en el desplegable (valores API de TikTok). */
const PRIVACY_LABEL: Record<string, string> = {
  PUBLIC_TO_EVERYONE: "Público",
  MUTUAL_FOLLOW_FRIENDS: "Amigos",
  FOLLOWER_OF_CREATOR: "Seguidores",
  SELF_ONLY: "Sólo yo",
};

/** Subtítulo explicativo bajo la opción elegida (similar a Metricool). */
const PRIVACY_DESCRIPTION: Record<string, string> = {
  PUBLIC_TO_EVERYONE: "Cualquiera puede ver esta publicación.",
  MUTUAL_FOLLOW_FRIENDS:
    "Solo podrán verla los seguidores a los que también sigues.",
  FOLLOWER_OF_CREATOR: "Solo tus seguidores en TikTok pueden ver esta publicación.",
  SELF_ONLY: "Nadie más puede ver esta publicación (privada en tu perfil).",
};

function hasTiktokSelected(providers: { id?: string; name?: string }[]) {
  return providers.some(
    (sp) =>
      String(sp?.id ?? sp?.name ?? "").toLowerCase() ===
      SocialProviderId.TikTok.toLowerCase()
  );
}

interface Props {
  companyId: string;
}

export const TiktokDirectPostPanel = ({ companyId }: Props) => {
  const dispatch = useDispatch();
  const { socialProviders, selectedPost, video } = useSocialPostCore();
  const creatorInfo = useSelector(tiktokCreatorInfoSelector);
  const loading = useSelector(tiktokCreatorInfoLoadingSelector);
  const loadError = useSelector(tiktokCreatorInfoErrorSelector);
  const draft = useSelector(tiktokDirectPostDraftSelector);

  const hasTiktok = useMemo(
    () => hasTiktokSelected(socialProviders as { id?: string; name?: string }[]),
    [socialProviders]
  );

  const isVideo = useMemo(
    () => inferComposerHasVideo(video, selectedPost),
    [video, selectedPost]
  );

  const videoDurationSec = video?.duration ?? null;

  useEffect(() => {
    if (!hasTiktok || !companyId) {
      dispatch(resetTiktokDirectPostUiAction());
      return;
    }
    let cancelled = false;
    dispatch(getTiktokCreatorInfoInitAction());
    tiktokApiService
      .getCreatorInfo(companyId)
      .then((info) => {
        if (!cancelled) {
          dispatch(getTiktokCreatorInfoSuccessAction({ creatorInfo: info }));
        }
      })
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo obtener la información del creador en TikTok.";
        if (!cancelled) {
          dispatch(getTiktokCreatorInfoFailureAction({ error: String(msg) }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hasTiktok, companyId, dispatch]);

  useEffect(() => {
    const p = selectedPost?.post;
    if (!p || p.socialProvider !== "tiktok" || !p.tiktokDirectPostPayload) {
      return;
    }
    try {
      const raw =
        typeof p.tiktokDirectPostPayload === "string"
          ? JSON.parse(p.tiktokDirectPostPayload)
          : p.tiktokDirectPostPayload;
      dispatch(
        hydrateTiktokDirectPostDraftAction({
          draft: {
            privacyLevel: raw.privacyLevel ?? null,
            allowComment: Boolean(raw.allowComment),
            allowDuet: Boolean(raw.allowDuet),
            allowStitch: Boolean(raw.allowStitch),
            commercialDisclosure: Boolean(raw.commercialDisclosure),
            brandOrganic: Boolean(raw.brandOrganic),
            brandContent: Boolean(raw.brandContent),
            musicUsageConsent: Boolean(raw.musicUsageConsent),
          },
        })
      );
    } catch {
      /* ignore invalid saved payload */
    }
  }, [selectedPost?.post?.id, selectedPost?.post?.tiktokDirectPostPayload, dispatch]);

  const nickname = creatorInfo?.creator_nickname ?? "—";
  const avatarUrl = creatorInfo?.creator_avatar_url;

  const privacyOptions = creatorInfo?.privacy_level_options ?? [];

  const commentBlockedByTiktok = tiktokCreatorFlagDisablesFeature(
    creatorInfo?.comment_disabled
  );
  /** TikTok creator_info puede marcar restricción; igual permitimos elegir (TikTok valida al publicar). */
  const duetRestrictedHint = tiktokCreatorFlagDisablesFeature(
    creatorInfo?.duet_disabled
  );
  const stitchRestrictedHint = tiktokCreatorFlagDisablesFeature(
    creatorInfo?.stitch_disabled
  );

  const declarationBlock = useMemo(() => {
    const d = draft;
    if (!d.musicUsageConsent) {
      return null;
    }
    const music = (
      <a
        href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
        target="_blank"
        rel="noopener noreferrer"
      >
        Confirmación de uso de música
      </a>
    );
    const bc = (
      <a
        href="https://www.tiktok.com/legal/page/global/bc-policy/en"
        target="_blank"
        rel="noopener noreferrer"
      >
        Política de contenido de marca
      </a>
    );
    if (!d.commercialDisclosure || (!d.brandOrganic && !d.brandContent)) {
      return (
        <p className="small text-muted mb-0">
          Al publicar, aceptás la {music} de TikTok.
        </p>
      );
    }
    if (d.brandContent && !d.brandOrganic) {
      return (
        <p className="small text-muted mb-0">
          Al publicar, aceptás la {bc} y la {music} de TikTok.
        </p>
      );
    }
    if (!d.brandContent && d.brandOrganic) {
      return (
        <p className="small text-muted mb-0">
          Al publicar, aceptás la {music} de TikTok.
        </p>
      );
    }
    return (
      <p className="small text-muted mb-0">
        Al publicar, aceptás la {bc} y la {music} de TikTok.
      </p>
    );
  }, [draft]);

  if (!hasTiktok) {
    return null;
  }

  const durationTooLong =
    isVideo &&
    videoDurationSec != null &&
    videoDurationSec > 0 &&
    creatorInfo?.max_video_post_duration_sec != null &&
    videoDurationSec > creatorInfo.max_video_post_duration_sec;

  return (
    <div className="tiktok-direct-post-panel border rounded p-3 mb-3 bg-light">
      <h6 className="font-weight-bold mb-3">Publicación en TikTok (Direct Post)</h6>
      <p className="small text-muted mb-2">
        Según las{" "}
        <a
          href="https://developers.tiktok.com/doc/content-sharing-guidelines#direct_post_api_-_developer_guidelines"
          target="_blank"
          rel="noopener noreferrer"
        >
          guías de TikTok
        </a>
        , tienes que elegir visibilidad, interacciones y declaración de contenido comercial antes de publicar.
      </p>

      {loading && (
        <Alert color="light" className="mb-2 py-2">
          Cargando cuenta y permisos de TikTok…
        </Alert>
      )}
      {loadError && !loading && (
        <Alert color="warning" className="mb-2">
          {loadError}
        </Alert>
      )}

      {!loading && creatorInfo && (
        <>
          <div className="d-flex align-items-center mb-3">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="rounded-circle mr-2"
                style={{ width: 40, height: 40, objectFit: "cover" }}
              />
            ) : null}
            <div>
              <div className="small text-muted">Publicarás en la cuenta</div>
              <div className="font-weight-bold">{nickname}</div>
            </div>
          </div>

          {durationTooLong && (
            <Alert color="danger" className="py-2">
              Este video supera el máximo permitido por TikTok para tu cuenta (
              {creatorInfo.max_video_post_duration_sec}s). Acorta el video o elige otro.
            </Alert>
          )}

          <FormGroup>
            <Label for="tiktok-privacy">¿Quién puede ver la publicación?</Label>
            <Input
              id="tiktok-privacy"
              type="select"
              value={draft.privacyLevel ?? ""}
              onChange={(e) => {
                const v = e.target.value || null;
                dispatch(setTiktokDirectPostDraftAction({ patch: { privacyLevel: v } }));
              }}
            >
              <option value="" disabled>
                Elige una opción…
              </option>
              {privacyOptions.map((opt) => (
                <option
                  key={opt}
                  value={opt}
                  disabled={
                    draft.brandContent && opt === "SELF_ONLY"
                  }
                >
                  {PRIVACY_LABEL[opt] ?? opt}
                  {draft.brandContent && opt === "SELF_ONLY"
                    ? " (no disponible con contenido de marca)"
                    : ""}
                </option>
              ))}
            </Input>
            {draft.privacyLevel && PRIVACY_DESCRIPTION[draft.privacyLevel] ? (
              <small className="form-text text-muted d-block mt-1">
                {PRIVACY_DESCRIPTION[draft.privacyLevel]}
              </small>
            ) : null}
          </FormGroup>

          <FormGroup>
            <Label className="d-block mb-1">Interacciones</Label>
            <small className="text-muted d-block mb-2">
              {isVideo ? (
                <>
                  Puedes permitir comentarios, dúo y pegar (Stitch) en{" "}
                  <strong>videos</strong>. Si TikTok tiene restricciones en tu cuenta, te lo
                  indicamos debajo; igual puedes marcar lo que quieras enviar en la publicación.
                </>
              ) : (
                <>
                  En <strong>fotos o carruseles</strong> solo aplica comentarios: la API de
                  TikTok no permite configurar dúo ni pegar (Stitch) para ese tipo de
                  publicación.
                </>
              )}
            </small>
            <div className="d-flex flex-column">
              <CustomInput
                className="mb-2"
                id="tiktok-allow-comment"
                type="checkbox"
                label="Permitir comentarios"
                checked={draft.allowComment}
                disabled={commentBlockedByTiktok}
                onChange={(e) =>
                  dispatch(
                    setTiktokDirectPostDraftAction({
                      patch: { allowComment: e.target.checked },
                    })
                  )
                }
              />
              {isVideo ? (
                <>
                  <CustomInput
                    className="mb-2"
                    id="tiktok-allow-duet"
                    type="checkbox"
                    label="Permitir dúo"
                    checked={draft.allowDuet}
                    onChange={(e) =>
                      dispatch(
                        setTiktokDirectPostDraftAction({
                          patch: { allowDuet: e.target.checked },
                        })
                      )
                    }
                  />
                  <CustomInput
                    className="mb-0"
                    id="tiktok-allow-stitch"
                    type="checkbox"
                    label="Permitir pegar (Stitch)"
                    checked={draft.allowStitch}
                    onChange={(e) =>
                      dispatch(
                        setTiktokDirectPostDraftAction({
                          patch: { allowStitch: e.target.checked },
                        })
                      )
                    }
                  />
                  {(duetRestrictedHint || stitchRestrictedHint) && (
                    <Alert color="light" className="small py-2 mb-0 mt-2">
                      TikTok indica restricciones en tu cuenta para{" "}
                      {duetRestrictedHint && stitchRestrictedHint
                        ? "dúo y pegar (Stitch)"
                        : duetRestrictedHint
                          ? "dúo"
                          : "pegar (Stitch)"}
                      . Puedes marcarlas igual; al publicar TikTok aplicará lo que corresponda.
                    </Alert>
                  )}
                </>
              ) : null}
            </div>
            <small className="text-muted d-block mt-2">
              Por defecto todo desactivado: activá solo lo que quieras permitir.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="tiktok-commercial" className="d-block mb-1">
              Contenido comercial
            </Label>
            <small className="text-muted d-block mb-2">
              Activá esta opción para indicar que este post promociona bienes o servicios a
              cambio de algo de valor (según las reglas de TikTok).
            </small>
            <CustomInput
              id="tiktok-commercial"
              type="switch"
              label="Indicar contenido comercial en esta publicación"
              checked={draft.commercialDisclosure}
              onChange={(e) => {
                const on = e.target.checked;
                dispatch(
                  setTiktokDirectPostDraftAction({
                    patch: {
                      commercialDisclosure: on,
                      brandOrganic: on ? draft.brandOrganic : false,
                      brandContent: on ? draft.brandContent : false,
                    },
                  })
                );
              }}
            />
            {draft.commercialDisclosure && (
              <div className="mt-2 pl-1 border-left border-primary">
                <CustomInput
                  id="tiktok-brand-organic"
                  type="checkbox"
                  label="Tu marca (contenido promocional propio)"
                  checked={draft.brandOrganic}
                  onChange={(e) =>
                    dispatch(
                      setTiktokDirectPostDraftAction({
                        patch: { brandOrganic: e.target.checked },
                      })
                    )
                  }
                />
                {draft.brandOrganic && (
                  <small className="d-block text-muted mb-2">
                    Tu foto/video se etiquetará como &quot;Contenido promocional&quot;.
                  </small>
                )}
                <CustomInput
                  id="tiktok-brand-content"
                  type="checkbox"
                  label="Contenido de marca (tercero / pago)"
                  checked={draft.brandContent}
                  onChange={(e) => {
                    const next = e.target.checked;
                    const patch: Record<string, unknown> = { brandContent: next };
                    if (next && draft.privacyLevel === "SELF_ONLY") {
                      const firstPublic = privacyOptions.find((o) => o !== "SELF_ONLY");
                      if (firstPublic) {
                        patch.privacyLevel = firstPublic;
                      }
                    }
                    dispatch(setTiktokDirectPostDraftAction({ patch }));
                  }}
                />
                {draft.brandContent && (
                  <small className="d-block text-muted mb-2">
                    Tu foto/video se etiquetará como &quot;Colaboración paga&quot; (o equivalente en TikTok).
                  </small>
                )}
                {draft.commercialDisclosure &&
                  !draft.brandOrganic &&
                  !draft.brandContent && (
                    <small className="text-warning d-block">
                      Activá al menos una opción para poder publicar con contenido comercial.
                    </small>
                  )}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label className="d-block mb-1">Uso de música y términos</Label>
            <small className="text-muted d-block mb-2">
              No es lo mismo que &quot;agregar música&quot; en otras herramientas: acá no elegís
              una canción obligatoria. Lo que sí pide TikTok para publicar por Direct Post es
              que <strong>confirmes</strong> haber leído y aceptado su normativa sobre uso de
              música y contenido (el enlace aparece al marcar la casilla).
            </small>
            <CustomInput
              id="tiktok-music-consent"
              type="checkbox"
              label="Confirmo haber leído y acepto lo indicado abajo (requerido para publicar)"
              checked={draft.musicUsageConsent}
              onChange={(e) =>
                dispatch(
                  setTiktokDirectPostDraftAction({
                    patch: { musicUsageConsent: e.target.checked },
                  })
                )
              }
            />
            {declarationBlock}
          </FormGroup>

          <p className="small text-muted mb-0">
            Después de publicar, el contenido puede tardar unos minutos en procesarse y verse en tu perfil de TikTok.
          </p>
        </>
      )}
    </div>
  );
};

export default TiktokDirectPostPanel;
